import type { Style } from "../shared/types";

export type GroupedStyle = Style & {
  groupKey: string;
  sourceStyles: Style[];
  adCount: number;
};

function normalizedName(value: string): string {
  return value.trim().toLocaleLowerCase().replace(/\s+/gu, " ");
}

export function styleGroupKey(style: Style): string {
  const signature = style.formatProfileSignature?.trim();
  if (signature) return `signature:${signature}`;
  const recipe = style.recipeId?.trim().toLocaleLowerCase();
  return recipe ? `recipe:${recipe}|name:${normalizedName(style.name)}` : `name:${normalizedName(style.name)}`;
}

export function groupStyles(styles: Style[]): GroupedStyle[] {
  const groups = new Map<string, Style[]>();
  for (const style of styles) {
    const key = styleGroupKey(style);
    const group = groups.get(key);
    if (group) group.push(style);
    else groups.set(key, [style]);
  }

  return [...groups.entries()].map(([groupKey, sourceStyles]) => {
    const primary = [...sourceStyles].sort((left, right) => {
      const leftHasAds = left.sources.some((source) => Boolean(source.ad));
      const rightHasAds = right.sources.some((source) => Boolean(source.ad));
      if (leftHasAds !== rightHasAds) return leftHasAds ? -1 : 1;
      if ((left.status === "active") !== (right.status === "active")) return left.status === "active" ? -1 : 1;
      return left.publicId.localeCompare(right.publicId);
    })[0];
    if (!primary) throw new Error("A Style group cannot be empty.");
    const sources = sourceStyles.flatMap((style) => style.sources);
    const ads = new Set(sources.map((source) => source.ad?.id).filter((id): id is string => Boolean(id)));
    const visualTraits = [...new Set(sourceStyles.flatMap((style) => style.visualTraits))];
    return {
      ...primary,
      groupKey,
      sourceStyles,
      sources,
      visualTraits,
      adCount: ads.size,
    };
  });
}
